<div class="container body-container">
  <div class="main-container">
    <!-- Header -->
    <div class="track-header mb-4">
      <h3 class="track-title">Shipment Tracking</h3>
      <p class="track-subtitle text-muted mb-0">Real-time status for your parcel</p>
    </div>

    <!-- Loading State -->
    <div class="row" *ngIf="loader">
      <div class="col-12">
        <div class="track-loading-card">
          <div class="spinner-border text-primary me-3" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
          <span class="text-muted">Fetching shipment details…</span>
        </div>
      </div>
    </div>

    <!-- Error State -->
    <div class="row" *ngIf="!loader && !status">
      <div class="col-12">
        <div class="track-error-card">
          <div class="track-error-icon">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="40"
              height="40"
              fill="currentColor"
              viewBox="0 0 16 16"
            >
              <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
              <path
                d="M7.002 11a1 1 0 1 1 2 0 1 1 0 0 1-2 0zM7.1 4.995a.905.905 0 1 1 1.8 0l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 4.995z"
              />
            </svg>
          </div>
          <div>
            <h5 class="mb-1">Shipment Not Found</h5>
            <p class="mb-0 text-muted">
              The AWB or reference number provided is invalid. Please verify and try again, or
              <a href="/contact">contact us</a> for help.
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Results -->
    <div *ngIf="!loader && status">
      <!-- Status Banner -->
      <div class="track-status-banner mb-4 tracking-status-intransit">
        <div class="d-flex align-items-center justify-content-between flex-wrap gap-2">
          <div>
            <div class="track-status-label">Current Status</div>
            <div class="track-status-value">{{ statusList[statusList.length - 1]?.status }}</div>
          </div>
          <div class="track-awb-badge">
            <span class="track-awb-label">AWB</span>
            <span class="track-awb-number">{{ trackResult.awbNumber }}</span>
          </div>
        </div>
      </div>

      <div class="row g-4">
        <!-- ── Shipment Details Card ── -->
        <div class="col-lg-5">
          <div class="track-card h-100">
            <div class="track-card-header">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                fill="currentColor"
                viewBox="0 0 16 16"
                class="me-2"
              >
                <path
                  d="M0 3.5A1.5 1.5 0 0 1 1.5 2h9A1.5 1.5 0 0 1 12 3.5V5h1.02a1.5 1.5 0 0 1 1.17.563l1.481 1.85a1.5 1.5 0 0 1 .329.938V10.5a1.5 1.5 0 0 1-1.5 1.5H14a2 2 0 1 1-4 0H5a2 2 0 1 1-3.998-.085A1.5 1.5 0 0 1 0 10.5v-7zm1.294 7.456A2 2 0 0 1 4.732 11h5.536a2 2 0 0 1 .732-.732V3.5a.5.5 0 0 0-.5-.5h-9a.5.5 0 0 0-.5.5v7a.5.5 0 0 0 .294.456zM12 10a2 2 0 0 1 1.732 1h.768a.5.5 0 0 0 .5-.5V8.35a.5.5 0 0 0-.11-.312l-1.48-1.85A.5.5 0 0 0 13.02 6H12v4zm-9 1a1 1 0 1 0 0 2 1 1 0 0 0 0-2zm9 0a1 1 0 1 0 0 2 1 1 0 0 0 0-2z"
                />
              </svg>
              Shipment Details
            </div>
            <div class="track-card-body">
              <dl class="track-detail-list">
                <div class="track-detail-row">
                  <dt>AWB Number</dt>
                  <dd>{{ trackResult.awbNumber }}</dd>
                </div>
                <div class="track-detail-row">
                  <dt>Reference Number</dt>
                  <dd>{{ trackResult.referenceNumber || '—' }}</dd>
                </div>
                <div class="track-detail-row">
                  <dt>Booked Date</dt>
                  <dd>{{ trackResult.bookedDate | date: 'medium' }}</dd>
                </div>

                <!-- Show full shipment details (also visible for 'link' mode) -->
                <div class="track-detail-row">
                  <dt>Origin</dt>
                  <dd>{{ trackResult.origin }}</dd>
                </div>
                <div class="track-detail-row">
                  <dt>Destination</dt>
                  <dd>{{ trackResult.destination }}</dd>
                </div>
                <div class="track-detail-row">
                  <dt>Package Type</dt>
                  <dd>{{ getDoxType(trackResult.doxType) }}</dd>
                </div>
                <div class="track-detail-row">
                  <dt>Shipment Mode</dt>
                  <dd>{{ getShipmentMode(trackResult.shipmentMode) }}</dd>
                </div>
                <div class="track-detail-row">
                  <dt>Transport Mode</dt>
                  <dd
                    [class.fw-bold]="
                      getTransportMode(trackResult.transportMode).toLowerCase().includes('surface')
                    "
                  >
                    {{ getTransportMode(trackResult.transportMode) }}
                  </dd>
                </div>
                <div class="track-detail-row" *ngIf="trackResult.receivedPerson">
                  <dt>Received By</dt>
                  <dd>{{ trackResult.receivedPerson }}</dd>
                </div>
                <div
                  class="track-detail-row"
                  *ngIf="
                    trackResult.receivedPersonRelation &&
                    trackResult.receivedPersonRelation !== 'NULL'
                  "
                >
                  <dt>Relation</dt>
                  <dd>{{ getStatusRelation(trackResult.receivedPersonRelation) }}</dd>
                </div>
                <div class="track-detail-row" *ngIf="trackResult.deliveryOfficeAddress">
                  <dt>Delivery Office</dt>
                  <dd>{{ trackResult.deliveryOfficeAddress }}</dd>
                </div>

                <div class="track-detail-row" *ngIf="trackResult.remarks">
                  <dt>Remarks</dt>
                  <dd class="fw-semibold">{{ trackResult.remarks }}</dd>
                </div>
                <div class="track-detail-row" *ngIf="trackResult.courierName">
                  <dt>Courier</dt>
                  <dd>{{ trackResult.courierName }}</dd>
                </div>

                <!-- Courier website link: only for 'link' and 'internal' modes with a track URL -->
                <div
                  class="track-detail-row"
                  *ngIf="getTrackUrl() && trackResult.courierMode !== 'link'"
                >
                  <dt>Courier Website</dt>
                  <dd>
                    <a [href]="getTrackUrl()" target="_blank" class="track-external-link">
                      Track on courier site
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="12"
                        height="12"
                        fill="currentColor"
                        viewBox="0 0 16 16"
                        class="ms-1"
                      >
                        <path
                          fill-rule="evenodd"
                          d="M8.636 3.5a.5.5 0 0 0-.5-.5H1.5A1.5 1.5 0 0 0 0 4.5v10A1.5 1.5 0 0 0 1.5 16h10a1.5 1.5 0 0 0 1.5-1.5V7.864a.5.5 0 0 0-1 0V14.5a.5.5 0 0 1-.5.5h-10a.5.5 0 0 1-.5-.5v-10a.5.5 0 0 1 .5-.5h6.636a.5.5 0 0 0 .5-.5z"
                        />
                        <path
                          fill-rule="evenodd"
                          d="M16 .5a.5.5 0 0 0-.5-.5h-5a.5.5 0 0 0 0 1h3.793L6.146 9.146a.5.5 0 1 0 .708.708L15 1.707V5.5a.5.5 0 0 0 1 0v-5z"
                        />
                      </svg>
                    </a>
                  </dd>
                </div>
              </dl>

              <!-- Link mode: prominent CTA button -->
              <div class="track-external-cta mt-3" *ngIf="trackResult.courierMode === 'link'">
                <a
                  *ngIf="getTrackUrl(); else refNotFoundBlock"
                  [href]="getTrackUrl()"
                  target="_blank"
                  class="btn btn-primary w-100"
                >
                  View Detailed Tracking
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    fill="currentColor"
                    viewBox="0 0 16 16"
                    class="ms-2"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M8.636 3.5a.5.5 0 0 0-.5-.5H1.5A1.5 1.5 0 0 0 0 4.5v10A1.5 1.5 0 0 0 1.5 16h10a1.5 1.5 0 0 0 1.5-1.5V7.864a.5.5 0 0 0-1 0V14.5a.5.5 0 0 1-.5.5h-10a.5.5 0 0 1-.5-.5v-10a.5.5 0 0 1 .5-.5h6.636a.5.5 0 0 0 .5-.5z"
                    />
                    <path
                      fill-rule="evenodd"
                      d="M16 .5a.5.5 0 0 0-.5-.5h-5a.5.5 0 0 0 0 1h3.793L6.146 9.146a.5.5 0 1 0 .708.708L15 1.707V5.5a.5.5 0 0 0 1 0v-5z"
                    />
                  </svg>
                </a>
                <ng-template #refNotFoundBlock>
                  <div class="alert alert-warning mb-0">
                    Reference number not available. Please <a href="/contact">contact us</a> for
                    details.
                  </div>
                </ng-template>
              </div>
            </div>
          </div>
        </div>

        <!-- ── Timeline Card (internal mode only) ── -->
        <div class="col-lg-7" *ngIf="trackResult.courierMode !== 'link'">
          <div class="track-card h-100">
            <div class="track-card-header">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                fill="currentColor"
                viewBox="0 0 16 16"
                class="me-2"
              >
                <path
                  d="M8 3.5a.5.5 0 0 0-1 0V9a.5.5 0 0 0 .252.434l3.5 2a.5.5 0 0 0 .496-.868L8 8.71V3.5z"
                />
                <path d="M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16zm7-8A7 7 0 1 1 1 8a7 7 0 0 1 14 0z" />
              </svg>
              Tracking History
            </div>
            <div class="track-card-body">
              <div class="track-timeline">
                <div
                  class="track-timeline-item"
                  *ngFor="let item of statusList; let last = last"
                  [class.track-timeline-item--latest]="last"
                >
                  <div class="track-timeline-dot" [class.track-timeline-dot--latest]="last"></div>
                  <div class="track-timeline-connector" *ngIf="!last"></div>
                  <div class="track-timeline-content">
                    <div class="track-timeline-status" [class.fw-bold]="last">
                      {{ item.status }}
                    </div>
                    <div class="track-timeline-remark text-muted" *ngIf="item.remark">
                      {{ item.remark }}
                    </div>
                    <div class="track-timeline-time text-muted">
                      <small>{{ item.statusDate }} &middot; {{ item.statusTime }}</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
